<template>
  <b-container>
    <b-card>
      <b-breadcrumb>
          <b-breadcrumb-item :to="{name: 'ward-list', params: { wardId: wardId}}">Back to {{ward.ward_name}}</b-breadcrumb-item>
          <b-breadcrumb-item active>Appointments</b-breadcrumb-item>
      </b-breadcrumb>
      <h3>{{ward.ward_name}}  Appointments</h3>
      <b-row>
        <b-col>
          <b-button class="mb-2" variant="primary" :to="{name: 'appointment-detail', params: { 'wardId': wardId}}" >Schedule <font-awesome-icon icon="plus" /></b-button>
        </b-col>
      </b-row>
        <b-row class="mb-3 mt-3">
            <b-col>
                  <b-input-group>
      <b-input-group-prepend is-text>
        <font-awesome-icon :icon="['fa', 'search']"/>
      </b-input-group-prepend>
                <b-form-input v-model="searchTerm" placeholder="Search for Appointments" @input="delaySearchAppointments"></b-form-input>
                  </b-input-group>
                <b-form-checkbox class="mt-2" v-model="filterByDate" name="check-button" variant="primary" switch>
                  {{(filterByDate)? "Hide appointments date filter": "Filter by appointment date"}}
                </b-form-checkbox>
            </b-col>
          <b-col>
                  <b-input-group>
      <b-input-group-prepend is-text>
        <font-awesome-icon :icon="['fa', 'filter']"/>
      </b-input-group-prepend>
            <b-form-select
                id="input-3"
                v-model="filterStatus"
                :options="filterStatusOptions"
                @change="resetPaginationAndSearch"
            ></b-form-select>
                  </b-input-group>
            </b-col>
        </b-row>
      <b-row>
          <b-col>
          <b-calendar
            v-if="filterByDate"
            variant="primary"
            v-model="dateFilterSearch"
            @context="onContext"
            locale="en-US"
            :hide-header="true"
            label-help="Use cursor keys to navigate appointment dates"
            @input="searchByDate"
            :value-as-date="true"
            :min="minDate"
          >
            <div class="d-flex" dir="ltr">
              <b-button
                size="sm"
                variant="outline-danger"
                v-if="dateFilterSearch"
                @click="clearTodayDate"
              >
                Clear date
              </b-button>
              <b-button
                size="sm"
                variant="outline-primary"
                class="ml-auto"
                @click="setTodayDate"
              >
                Set Today
              </b-button>
            </div>
          </b-calendar>
          </b-col>
      </b-row>
        <!-- Loading Spinner -->
        <b-row v-if="table.isBusy">
            <b-col class="text-center my-5">
                <b-spinner variant="primary" label="Loading..."></b-spinner>
                <p class="mt-2">Loading appointments...</p>
            </b-col>
        </b-row>

        <!-- Empty State -->
        <b-row v-else-if="!table.isBusy && table.items.length === 0">
            <b-col class="text-center my-5">
                <p class="text-muted">No Appointments to show</p>
            </b-col>
        </b-row>

        <!-- Card-Based Timeline View -->
        <div v-else class="appointments-timeline">
            <div v-for="(appointments, date) in groupedAppointments" :key="date" class="date-group mb-4">
                <!-- Date Header -->
                <div class="date-header mb-3">
                    <h5 class="mb-0">
                        <b-badge variant="primary" class="date-badge">
                            {{getRelativeDateLabel(date)}}
                        </b-badge>
                        <span class="ml-2 text-muted">{{$moment(date).format('dddd, MMMM D, YYYY')}}</span>
                    </h5>
                </div>

                <!-- Appointment Cards -->
                <b-row>
                    <b-col 
                        v-for="appointment in appointments" 
                        :key="appointment.appointment_id"
                        cols="12"
                        md="6"
                        lg="4"
                        class="mb-3">
                        <b-card 
                            :class="['appointment-card', `border-${getStatusVariant(appointment.appointment_state, appointment.appointment_date)}`]"
                            :border-variant="getStatusVariant(appointment.appointment_state, appointment.appointment_date)">
                            
                            <!-- Card Header: Time and Status -->
                            <template #header>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="appointment-time">
                                        <font-awesome-icon :icon="['fa', 'clock']" class="mr-1"/>
                                        <strong>{{$moment.utc(appointment.appointment_date).local().format('h:mm A')}}</strong>
                                    </div>
                                    <b-badge :variant="getStatusVariant(appointment.appointment_state, appointment.appointment_date)">
                                        {{appointment.appointment_state}}
                                    </b-badge>
                                </div>
                            </template>

                            <!-- Card Body: Appointment Details -->
                            <b-card-text>
                                <!-- Appointment Type/Reason -->
                                <div class="appointment-detail mb-2">
                                    <font-awesome-icon :icon="['fa', 'calendar-check']" class="text-muted mr-2"/>
                                    <strong>{{getAppointmentTypeDescription(appointment.appointment_type)}}</strong>
                                    <span v-if="appointment.appointment_type==='OTHER' && appointment.appointment_other_reason">
                                        - {{appointment.appointment_other_reason}}
                                    </span>
                                </div>

                                <!-- Organizer -->
                                <div class="appointment-detail mb-2">
                                    <font-awesome-icon :icon="['fa', 'user']" class="text-muted mr-2"/>
                                    <span v-if="appointment.organizing_contact">
                                        {{(appointment.organizing_contact.title)?appointment.organizing_contact.title + " ": ""}}{{appointment.organizing_contact.first_name}} {{appointment.organizing_contact.last_name}}
                                    </span>
                                    <span v-else class="text-muted font-italic">Public Appointment</span>
                                </div>

                                <!-- Location -->
                                <div class="appointment-detail mb-2">
                                    <font-awesome-icon :icon="['fa', 'map-marker-alt']" class="text-muted mr-2"/>
                                    {{appointment.appointment_location}}
                                </div>

                                <!-- Attendees -->
                                <div class="appointment-detail" v-if="appointment.attendee_contacts && appointment.attendee_contacts.length > 0">
                                    <font-awesome-icon :icon="['fa', 'users']" class="text-muted mr-2"/>
                                    <small class="text-muted">{{flattenOutAttendees(appointment.attendee_contacts)}}</small>
                                </div>
                            </b-card-text>

                            <!-- Card Footer: Quick Actions -->
                            <template #footer>
                                <!-- PENDING appointments (from public booking) -->
                                <div v-if="appointment.appointment_state === 'PENDING'" class="d-flex justify-content-between">
                                    <b-button
                                        size="sm"
                                        variant="primary"
                                        @click="quickScheduleAppointment(appointment.appointment_id)"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="check-circle" class="mr-1"/>
                                        Confirm
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="outline-primary"
                                        :to="{name: 'appointment-detail', params: { 'wardId': wardId, 'appointmentId': appointment.appointment_id}}"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="edit" class="mr-1"/>
                                        Edit
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="outline-danger"
                                        @click="quickCancelAppointment(appointment.appointment_id)"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="times" class="mr-1"/>
                                        Cancel
                                    </b-button>
                                </div>
                                
                                <!-- SCHEDULED appointments -->
                                <div v-else-if="appointment.appointment_state === 'SCHEDULED'" class="d-flex justify-content-between">
                                    <b-button
                                        size="sm"
                                        variant="success"
                                        @click="quickCompleteAppointment(appointment.appointment_id)"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="check" class="mr-1"/>
                                        Complete
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="outline-primary"
                                        :to="{name: 'appointment-detail', params: { 'wardId': wardId, 'appointmentId': appointment.appointment_id}}"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="edit" class="mr-1"/>
                                        Edit
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="outline-danger"
                                        @click="quickCancelAppointment(appointment.appointment_id)"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="times" class="mr-1"/>
                                        Cancel
                                    </b-button>
                                </div>
                                
                                <!-- Other statuses (COMPLETED, CANCELLED, etc.) - Edit only -->
                                <div v-else class="d-flex justify-content-center">
                                    <b-button
                                        size="sm"
                                        variant="outline-primary"
                                        :to="{name: 'appointment-detail', params: { 'wardId': wardId, 'appointmentId': appointment.appointment_id}}"
                                        class="quick-action-btn">
                                        <font-awesome-icon icon="edit" class="mr-1"/>
                                        Edit
                                    </b-button>
                                </div>
                            </template>
                        </b-card>
                    </b-col>
                </b-row>
            </div>
        </div>
        <b-row>
            <b-col align="center">
                <b-row>
            <b-col align="center">
                <b-button-group v-if="pages.length>1">
                    <b-button @click="searchAppointments(currentPage-1)">Prev</b-button>
                    <b-button @click="searchAppointments(currentPage+1)">Next</b-button>
                </b-button-group>
            </b-col>
        </b-row>
        </b-col>
        </b-row>
    </b-card>
  </b-container>
</template>
<script>
import { AppointmentApi } from "@/app/appointments/appointmentApi";
import handleError from "@/errorHandler";
import _ from "lodash";

export default {
  name: "AppointmentList",
  props: {
    wardId: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      ward: {
        ward_name: null
      },
      searchTerm: null,
      nextPage: {
        aux_id: null,
        pk_id: null,
        gsi_pk: null,
        gsi_sk: null,
        page_size: 10,
        search_term: null
      },
      prevPage: {
        aux_id: null,
        pk_id: null,
        gsi_pk: null,
        gsi_sk: null,
        page_size: 10,
        search_term: null
      },
      pages: [],
      currentPage: 0,
      lastPage: 0,
      dateFilterSearch: null,
      filterByDate: false,
      filterStatus: "SCHEDULED",
      filterStatusOptions: [],
      minDate: null,
      table: {
        id: "appointment-list",
        fields: [
          {
            key: "appointment_date",
            label: "Scheduled For"
          },
          {
            key: "appointment_type",
            label: "Reason",
          },
          {
            key: "organizing_contact",
            label: "With Who"
          },
          {
            key: "appointment_location",
            label: "Where"
          },
          {
            key: "attendee_contacts",
            label: "Attendee/s"
          },
          {
            key: "action",
            label: "Edit"
          }
        ],
        items: [],
        isBusy: false,
        totalRows: 0,
        page: {
          index: 0,
          size: 10
        },
      }
    }
  },
  computed: {
    groupedAppointments() {
      return this.groupAppointmentsByDate(this.table.items);
    }
  },
  async mounted () {
    this.pages.push(this.nextPage);
    this.minDate = new Date();
    this.filterStatusOptions.push({
      value: null,
      text: "Select a appointment filter"
    })
    this.filterStatusOptions = this.filterStatusOptions.concat(this.$store.state.frontEndSettings.appointment_states);
    await this.searchAppointments(0);
  },
  methods: {
    onContext(ctx) {
    },
    async searchByDate(val){
      await this.resetPaginationAndSearch();
    },
    clearTodayDate() {
      this.dateFilterSearch = null;
    },
    setTodayDate(){
        const now = new Date()
        this.dateFilterSearch = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    },
    async searchAppointments(page) {
      if (page < 0) {
        return;
      }
      if (page > this.pages.length - 1) {
        return;
      }
      let payload = this.pages[page]
      this.table.isBusy = true;
      payload.search_term = this.searchTerm;
      if (this.dateFilterSearch !== null && this.filterByDate) {
        let d = new Date();
        if (this.dateFilterSearch.getDate() === d.getDate()) {
          this.dateFilterSearch.setHours(d.getHours());
        }
        payload.date_filter_value = this.toIsoString(this.dateFilterSearch);
      } else {
        payload.date_filter_value = null;
        this.clearTodayDate();
      }
      if(this.filterStatus !== null && this.filterStatusOptions.length > 0){
        payload.appointment_state = this.filterStatus;
      }
      try {
        const { data } = await AppointmentApi.searchAppointments(this.wardId, payload);
        if (data.last_evaluated_key !== null) {
          if (!this.existInPageList(data.last_evaluated_key, this.pages))
            this.pages.push(data.last_evaluated_key);
        }
        this.table.items = data.result;
        this.table.isBusy = false;
        this.currentPage = page;
        this.ward = data.ward;
      } catch (e) {
        handleError(e, "Failed to fetch Appointments")
      }

    },
    existInPageList(obj, list) {
      if(list.length==0) return false;
      var i;
      for (i = 0; i < list.length; i++) {
        if(list[i].pk_id === obj.pk_id && list[i].aux_id === obj.aux_id) {
          return true;
        }
      }
      return false;
    },
    async filterAppointments() {
      this.pages = [];
      this.currentPage = 0;
      this.lastPage = 0;
      this.pages.push(this.nextPage);
      await this.searchAppointments(0);
    },
    flattenOutAttendees(attendees) {
      let out_response = []
      attendees.forEach((attendee) => {
        out_response.push(`${attendee.first_name} ${attendee.last_name} (${attendee.phone_number})`);
      })
      return out_response.join(', ');
    },
    getAppointmentTypeDescription(appointmentType) {
      if (!appointmentType) return 'No reason specified';
      
      // Look up in Vuex store
      const typeOption = this.$store.state.frontEndSettings.ward_appointment_types.find(
        opt => opt.value === appointmentType
      );
      
      if (typeOption) {
        return typeOption.text;
      }
      
      // Fallback: format the enum value (e.g., "BISHOP" -> "Bishop")
      return appointmentType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    delaySearchAppointments: _.debounce(async function() {
      await this.resetPaginationAndSearch();
    }, 500),
    async resetPaginationAndSearch() {
      // Reset pagination when filters or search term changes
      this.pages = [];
      this.currentPage = 0;
      this.lastPage = 0;
      // Push a fresh nextPage without any previous pagination keys
      this.pages.push({
        aux_id: null,
        pk_id: null,
        gsi_pk: null,
        gsi_sk: null,
        page_size: 10,
        search_term: null
      });
      await this.searchAppointments(0);
    },
    toIsoString(date) {
        var tzo = -date.getTimezoneOffset(),
            dif = tzo >= 0 ? '+' : '-',
            pad = function(num) {
                return (num < 10 ? '0' : '') + num;
            };

        return date.getFullYear() +
            '-' + pad(date.getMonth() + 1) +
            '-' + pad(date.getDate()) +
            'T' + pad(date.getHours()) +
            ':' + pad(date.getMinutes()) +
            ':' + pad(date.getSeconds()) +
            dif + pad(Math.floor(Math.abs(tzo) / 60)) +
            ':' + pad(Math.abs(tzo) % 60);
    },
    groupAppointmentsByDate(appointments) {
      const grouped = {};
      
      // Sort appointments by date first
      const sorted = [...appointments].sort((a, b) => {
        return new Date(a.appointment_date) - new Date(b.appointment_date);
      });

      // Group by date
      sorted.forEach(appointment => {
        const dateKey = this.$moment(appointment.appointment_date).format('YYYY-MM-DD');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(appointment);
      });

      return grouped;
    },
    getRelativeDateLabel(dateString) {
      const date = this.$moment(dateString);
      const today = this.$moment().startOf('day');
      const tomorrow = this.$moment().add(1, 'day').startOf('day');
      const yesterday = this.$moment().subtract(1, 'day').startOf('day');

      if (date.isSame(today, 'day')) {
        return 'Today';
      } else if (date.isSame(tomorrow, 'day')) {
        return 'Tomorrow';
      } else if (date.isSame(yesterday, 'day')) {
        return 'Yesterday';
      } else {
        return date.format('MMMM D');
      }
    },
    getStatusVariant(status, appointmentDate) {
      const now = new Date();
      const apptDate = new Date(appointmentDate);

      // Check if appointment is overdue
      if (status === 'SCHEDULED' && apptDate < now) {
        return 'danger'; // Overdue
      }

      // Check if appointment is today
      const isToday = this.$moment(appointmentDate).isSame(this.$moment(), 'day');
      if (status === 'SCHEDULED' && isToday) {
        return 'info'; // Today's appointments
      }

      // Status-based colors
      switch (status) {
        case 'SCHEDULED':
          return 'success'; // Upcoming scheduled
        case 'COMPLETED':
        case 'EXPIRED':
          return 'secondary'; // Completed/Done
        case 'CANCELLED':
          return 'secondary'; // Cancelled
        case 'PENDING':
          return 'warning'; // Needs attention
        default:
          return 'secondary';
      }
    },
    async quickCompleteAppointment(appointmentId) {
      try {
        // Find the appointment
        const appointment = this.table.items.find(a => a.appointment_id === appointmentId);
        if (!appointment) return;

        // Update the appointment state
        appointment.appointment_state = 'EXPIRED';
        appointment.ward_id = this.wardId;

        // Call API to update
        await AppointmentApi.updateAppointmentDetail(this.wardId, appointmentId, appointment);
        
        this.$toast.success('Appointment marked as complete');
        
        // Refresh the list
        await this.searchAppointments(this.currentPage);
      } catch (e) {
        handleError(e, 'Failed to complete appointment');
      }
    },
    async quickScheduleAppointment(appointmentId) {
      try {
        // Find the appointment
        const appointment = this.table.items.find(a => a.appointment_id === appointmentId);
        if (!appointment) return;

        // Update the appointment state from PENDING to SCHEDULED
        appointment.appointment_state = 'SCHEDULED';
        appointment.ward_id = this.wardId;

        // Call API to update
        await AppointmentApi.updateAppointmentDetail(this.wardId, appointmentId, appointment);
        
        this.$toast.success('Appointment confirmed');
        
        // Refresh the list
        await this.searchAppointments(this.currentPage);
      } catch (e) {
        handleError(e, 'Failed to schedule appointment');
      }
    },
    async quickCancelAppointment(appointmentId) {
      // Confirm before canceling
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to cancel this appointment?',
        {
          title: 'Cancel Appointment',
          okVariant: 'danger',
          okTitle: 'Yes, Cancel',
          cancelTitle: 'No, Keep It',
          centered: true
        }
      );

      if (!confirmed) return;

      try {
        await AppointmentApi.deleteAppointment(this.wardId, appointmentId);
        this.$toast.success('Appointment cancelled successfully');
        
        // Refresh the list
        await this.searchAppointments(this.currentPage);
      } catch (e) {
        handleError(e, 'Failed to cancel appointment');
      }
    }
  }
}
</script>
<style lang="scss" scoped>
/* Timeline View Styles */
.appointments-timeline {
  margin-top: 1rem;
}

.date-group {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.date-header {
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 1rem;
}

.date-badge {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

/* Appointment Card Styles */
.appointment-card {
  height: 100%;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left-width: 4px !important;
}

.appointment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.appointment-card .card-header {
  background-color: #f8f9fa;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0,0,0,0.125);
}

.appointment-time {
  font-size: 1.1rem;
  color: #495057;
}

.appointment-detail {
  display: flex;
  align-items: flex-start;
  font-size: 0.95rem;
  line-height: 1.5;
}

.appointment-detail svg {
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.appointment-card .card-footer {
  background-color: #fff;
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(0,0,0,0.125);
}

/* Quick Action Buttons */
.quick-action-btn {
  min-width: 44px;
  min-height: 44px;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

/* Mobile responsiveness */
@media (max-width: 767px) {
  /* Ensure card footer is visible */
  .appointment-card .card-footer {
    display: block !important;
    visibility: visible !important;
    overflow: visible !important;
    padding: 0.75rem !important;
  }

  /* Fix button container layout */
  .appointment-card .card-footer .d-flex {
    display: flex !important;
    flex-direction: column !important;
    gap: 0.5rem !important;
    width: 100% !important;
  }

  /* Make buttons full width and visible */
  .appointment-card .card-footer .quick-action-btn {
    width: 100% !important;
    display: block !important;
    visibility: visible !important;
    margin: 0 !important;
  }

  /* Ensure card doesn't clip footer */
  .appointment-card {
    overflow: visible !important;
  }

  /* Ensure appointment details are visible */
  .appointment-card .card-body .appointment-detail {
    display: flex !important;
    visibility: visible !important;
    overflow: visible !important;
  }
  
  .appointment-card .card-body .appointment-detail strong {
    display: inline-block !important;
    word-break: break-word;
  }

  .date-header h5 {
    font-size: 1rem;
  }

  .date-header .text-muted {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.875rem;
  }
}

@media (min-width: 768px) and (max-width: 991px) {
  /* Tablet: 2 columns */
  .appointment-card {
    margin-bottom: 1rem;
  }
}

@media (min-width: 992px) {
  /* Desktop: 3 columns */
  .appointment-card {
    margin-bottom: 1rem;
  }
}

/* Status-specific border colors enhancement */
.appointment-card.border-success {
  border-left-color: #28a745 !important;
}

.appointment-card.border-info {
  border-left-color: #17a2b8 !important;
}

.appointment-card.border-danger {
  border-left-color: #dc3545 !important;
}

.appointment-card.border-warning {
  border-left-color: #ffc107 !important;
}

.appointment-card.border-secondary {
  border-left-color: #6c757d !important;
}

/* Loading and empty states */
.text-center {
  padding: 2rem 0;
}
</style>
